// Page 1's deponent and enterprise details (owner, 2026-10-10): typed on the Company Details page (or,
// later, filled in from Xero) and printed in bold into page 1's table. The backend (server.py) checks and
// prints the same: _validate_details, sa_id_problem, detail_values, fit_detail.
import FONT from "@/data/detail-font.json";

export const isSpecialised = (slug) => slug.startsWith("specialised");

export const PROVINCES = [
  "Eastern Cape", "Free State", "Gauteng", "KwaZulu-Natal", "Limpopo",
  "Mpumalanga", "North West", "Northern Cape", "Western Cape",
];

// Specialised Entity affidavits: "Type of Entity (NPO, PBO etc.)". The value is what is written on the
// affidavit; "Other" lets the user type it.
export const ENTITY_TYPES = [
  { value: "NPO", label: "NPO (Non-Profit Organisation)" },
  { value: "PBO", label: "PBO (Public Benefit Organisation)" },
  { value: "NPO and PBO", label: "NPO and PBO" },
  { value: "NPC", label: "NPC (Non-Profit Company)" },
  { value: "Trust", label: "Trust" },
  { value: "Other", label: "Other (type it in)" },
];

export const NOT_APPLICABLE = "Not applicable"; // no VAT number (owner)

export const EMPTY_DETAILS = {
  deponent_name: "", id_number: "",
  enterprise_name: "", trading_name: "", registration_number: "", vat_number: "", nature_of_business: "",
  street: "", suburb: "", city: "", province: "", postal_code: "",
  entity_type: "", entity_type_other: "",
  // From Xero only (not sent): an Income Tax number in the contact's Tax field - no VAT number then
  income_tax_number: "",
};

export const ADDRESS_PARTS = ["street", "suburb", "city", "province", "postal_code"];

export const MAX_LEN = {
  deponent_name: 120, id_number: 13, enterprise_name: 150, trading_name: 150, registration_number: 14,
  vat_number: 10, nature_of_business: 150, street: 120, suburb: 80, city: 80, postal_code: 4,
  entity_type_other: 60,
};

const clean = (s) => (s || "").replace(/\s+/g, " ").trim();

// ---------------------------------------------------------------------------
// The deponent's Full name & Surname (owner, 2026-10-11, as on the CIPC site): a legal document, so the
// full name - at least a name and a surname, no initials, no full stops. Letters, spaces, hyphens (double-
// barrel names) and apostrophes (D'Oliveira, Ng'ang'a). The backend checks the same (server.py
// person_name_problem / proper_name).
// ---------------------------------------------------------------------------
const APOSTROPHES = /[’‘`´]/g;
// Surname particles stay lower-case inside a name ("Jan van der Merwe"); first or last they get a capital
const PARTICLES = new Set(["van", "der", "den", "de", "du", "le", "la", "von", "te", "ter", "da", "das", "dos", "del", "di"]);
const isUpper = (c) => c !== c.toLowerCase();

// Typing: periods, colons, semicolons, digits and other symbols are removed straight away
export const typePersonName = (value) =>
  (value || "").replace(APOSTROPHES, "'").replace(/[^\p{L}\s'-]/gu, "").replace(/^\s+/, "").replace(/\s{2,}/g, " ");

const properPart = (p) => {
  if (!p) return p;
  // O'Neill, D'Oliveira (one letter before the apostrophe); Ng'ang'a stays as it is
  if (p.length > 2 && p[1] === "'") return `${p[0].toUpperCase()}'${p[2].toUpperCase()}${p.slice(3)}`;
  // McDonald, McKenzie - not Mchunu, Mcwabe or Mcebisi (Mc + h / w / a vowel)
  if (/^mc[bcdfgjklmnpqrstvxyz]/.test(p) && p.length > 3) return `Mc${p[2].toUpperCase()}${p.slice(3)}`;
  return p[0].toUpperCase() + p.slice(1);
};

// One word in proper case. A word the user typed with capitals of their own (McDonald, MacKenzie) is kept.
const properWord = (word, i, count) => {
  if (isUpper(word[0]) && [...word.slice(1)].some(isUpper) && [...word].some((c) => !isUpper(c) && /\p{L}/u.test(c))) {
    return word;
  }
  const lower = word.toLowerCase();
  if (i > 0 && i < count - 1 && PARTICLES.has(lower)) return lower;
  return lower.split("-").map(properPart).join("-");
};

// When the field is left (and for names from Xero): tidied and in proper case - "jan VAN der merwe-smith"
// -> "Jan van der Merwe-Smith". Full stops from Xero ("J.P. Smith") become spaces, so the initials show.
export const tidyPersonName = (value) => {
  const words = (value || "").replace(APOSTROPHES, "'").replace(/[^\p{L}\s'-]/gu, " ").replace(/\s*-\s*/g, "-")
    .replace(/-{2,}/g, "-").replace(/'{2,}/g, "'").split(/\s+/).map((w) => w.replace(/^[-']+|[-']+$/g, "")).filter(Boolean);
  return words.map((w, i) => properWord(w, i, words.length)).join(" ");
};

// The single letters in the name (initials), e.g. ["J", "P"] for "J P Smith" or "J-P Smith"
export const nameInitials = (value) =>
  tidyPersonName(value).split(/[\s-]/).filter((w) => w.replace(/'/g, "").length === 1);

export const personNameProblem = (value) => {
  const name = tidyPersonName(value);
  if (!name) return "";
  if (name.split(" ").length < 2) return "Please enter the full name and the surname: the surname is missing.";
  const initials = nameInitials(name);
  if (initials.length) {
    return `Initials aren't allowed on this legal document: write ${initials.map((i) => `"${i}"`).join(", ")} out as the full name, as on the ID document.`;
  }
  return "";
};

// ---------------------------------------------------------------------------
// South African ID number: the Personal Tax site's check (PageOne.jsx validateSAIdNumber)
// ---------------------------------------------------------------------------
const luhnOk = (id) => {
  let sum = 0;
  let second = false;
  for (let i = id.length - 1; i >= 0; i -= 1) {
    let d = Number(id[i]);
    if (second) {
      d *= 2;
      if (d > 9) d = Math.floor(d / 10) + (d % 10);
    }
    sum += d;
    second = !second;
  }
  return sum % 10 === 0;
};

// "" when valid, else the error shown in red
export const idProblem = (value, today = new Date()) => {
  const id = (value || "").replace(/[\s-]/g, "");
  if (!/^\d{13}$/.test(id)) return "ID number must be exactly 13 digits (no letters or special characters)";
  const yy = Number(id.slice(0, 2));
  const mm = Number(id.slice(2, 4));
  const dd = Number(id.slice(4, 6));
  if (mm < 1 || mm > 12) return `Invalid month in ID number: ${id.slice(2, 4)}. Month must be between 01 and 12`;
  const fullYear = yy <= (today.getFullYear() % 100) + 10 ? 2000 + yy : 1900 + yy;
  const leap = (fullYear % 4 === 0 && fullYear % 100 !== 0) || fullYear % 400 === 0;
  const maxDay = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][mm - 1];
  if (dd < 1 || dd > maxDay) {
    return `Invalid day in ID number: ${id.slice(4, 6)}. Day must be between 01 and ${maxDay} for month ${id.slice(2, 4)}`;
  }
  if (new Date(fullYear, mm - 1, dd) > today) return "Date of birth cannot be in the future";
  const citizenship = id[10];
  if (!["0", "1", "2"].includes(citizenship)) {
    return `Invalid citizenship digit: ${citizenship}. Must be 0 (SA Citizen), 1 (Permanent Resident), or 2 (Refugee)`;
  }
  if (!luhnOk(id)) return "Invalid ID number: Check digit validation failed";
  return "";
};

// ---------------------------------------------------------------------------
// Registration (CIPC), VAT and postal code
// ---------------------------------------------------------------------------
// Typing: digits only, the slashes are put in (2015/123456/07)
export const formatRegistration = (value) => {
  const d = (value || "").replace(/\D/g, "").slice(0, 12);
  return [d.slice(0, 4), d.slice(4, 10), d.slice(10, 12)].filter(Boolean).join("/");
};

export const registrationProblem = (value, today = new Date()) => {
  const m = /^(\d{4})\/\d{6}\/\d{2}$/.exec(value || "");
  if (!m || Number(m[1]) < 1800 || Number(m[1]) > today.getFullYear()) {
    return "Enter the CIPC registration number as YYYY/NNNNNN/NN, e.g. 2015/123456/07.";
  }
  return "";
};

export const vatProblem = (value) =>
  !value || /^4\d{9}$/.test(value) ? "" : "A VAT number has 10 digits and starts with 4.";

export const postalProblem = (value) => (/^\d{4}$/.test(value || "") ? "" : "A postal code has 4 digits.");

// Characters the details font can print (others would print as empty boxes)
export const unwritable = (text) => [...new Set([...(text || "")].filter((c) => !(c in FONT.widths) && c !== "\n"))];

const REQUIRED = "This field is required.";

// {field: problem} for every field that isn't right yet (empty = all fine)
export const detailsProblems = (d, slug) => {
  const specialised = isSpecialised(slug);
  const out = {};
  const required = ["deponent_name", "id_number", "enterprise_name", "trading_name", "registration_number",
    "nature_of_business", ...ADDRESS_PARTS];
  required.forEach((k) => { if (!clean(d[k])) out[k] = REQUIRED; });
  // Owner: chosen from the SIC 7 list only - what is typed is just the search (backend checks the list)
  if (out.nature_of_business) out.nature_of_business = "Choose the Nature of Business from the list. Typing the SIC code also works e.g. 60100.";
  if (specialised) {
    if (!d.entity_type) out.entity_type = REQUIRED;
    else if (d.entity_type === "Other" && !clean(d.entity_type_other)) out.entity_type_other = REQUIRED;
  }
  if (!out.deponent_name && personNameProblem(d.deponent_name)) out.deponent_name = personNameProblem(d.deponent_name);
  if (!out.id_number && idProblem(d.id_number)) out.id_number = idProblem(d.id_number);
  if (!out.registration_number && registrationProblem(d.registration_number)) {
    out.registration_number = registrationProblem(d.registration_number);
  }
  if (!specialised && vatProblem(d.vat_number)) out.vat_number = vatProblem(d.vat_number);
  if (!out.postal_code && postalProblem(d.postal_code)) out.postal_code = postalProblem(d.postal_code);
  if (!out.province && d.province && !PROVINCES.includes(d.province)) out.province = "Choose the province.";
  Object.keys(EMPTY_DETAILS).forEach((k) => {
    const bad = unwritable(d[k]);
    if (!out[k] && bad.length) out[k] = `This character can't be printed on the affidavit: ${bad.join(" ")}`;
  });
  return out;
};

// What is sent to the backend (cleaned)
export const detailsPayload = (d, slug) => ({
  deponent_name: tidyPersonName(d.deponent_name),
  id_number: d.id_number.replace(/[\s-]/g, ""),
  enterprise_name: clean(d.enterprise_name),
  trading_name: clean(d.trading_name),
  registration_number: d.registration_number,
  vat_number: isSpecialised(slug) ? "" : d.vat_number,
  nature_of_business: clean(d.nature_of_business),
  street: clean(d.street),
  suburb: clean(d.suburb),
  city: clean(d.city),
  province: d.province,
  postal_code: d.postal_code,
  entity_type: isSpecialised(slug) ? (d.entity_type === "Other" ? clean(d.entity_type_other) : d.entity_type) : "",
});

// What is printed in page 1's table, by row (backend: detail_values)
export const detailValues = (p, slug) => ({
  deponent_name: p.deponent_name,
  id_number: p.id_number,
  enterprise_name: p.enterprise_name,
  trading_name: p.trading_name,
  registration_number: p.registration_number,
  vat_number: p.vat_number || NOT_APPLICABLE,
  nature_of_business: p.nature_of_business,
  // The address's parts, one per line break: fitDetail lays them out by rows (owner, 2026-10-11)
  address: ADDRESS_PARTS.map((k) => clean(p[k])).filter(Boolean).join("\n"),
  entity_type: isSpecialised(slug) ? p.entity_type : "",
});

// ---------------------------------------------------------------------------
// Printed in a table cell (owner: Carlito Bold - readable, Calibri's shapes as the affidavit itself;
// handwriting is only for the %s, dates and signatures): left-aligned, as large as DETAIL_FONT_MAX (or
// the row height allows), wrapped onto as many lines as the cell holds, smaller when it doesn't fit.
// The backend's fit_detail does exactly the same with the same character widths (detail-font.json),
// so page and PDF agree.
// ---------------------------------------------------------------------------
export const DETAIL_FONT_MAX = 11;      // backend DETAIL_FONT_MAX
const DETAIL_SIZE_PER_ROW = 0.75;       // part of a one-line row's height
const DETAIL_PAD_X = 5;
const DETAIL_LINE_GAP = 1.2;            // line height (x font size)
const DETAIL_SHRINK = 0.92;
const DETAIL_MIN = 4;
const DETAIL_CAP = 0.65;                // capital letters' height (x font size): centred in the cell

const textWidth = (text, size) => ([...text].reduce((w, c) => w + (FONT.widths[c] || 0), 0) * size) / 1000;

const wrap = (words, size, avail) => {
  const lines = [];
  let line = words[0];
  for (let i = 1; i < words.length; i += 1) {
    const candidate = `${line} ${words[i]}`;
    if (textWidth(candidate, size) <= avail) line = candidate;
    else {
      lines.push(line);
      line = words[i];
    }
  }
  lines.push(line);
  return lines;
};

// The text size for an affidavit's details: from its one-line rows (the Enterprise Name row)
export const detailBaseSize = (details) => Math.min(DETAIL_FONT_MAX, details.enterprise_name.h * DETAIL_SIZE_PER_ROW);

// The address (owner, 2026-10-11): from the top of its box, at the full size - each part on its own line
// until the box's last line, which holds the rest: 3 lines (22 affidavits) street / suburb / "city,
// province, postal code"; 4 lines (Specialised) street / suburb / city / "province, postal code"
const DETAIL_TOP_PAD = 3.5;             // address: space above its first line
export const addressRows = (parts, room) =>
  room > 1 && parts.length > room ? [...parts.slice(0, room - 1), parts.slice(room - 1).join(", ")] : parts;

// -> {size, lines: [{x, y, text}]} (y = baseline, PDF points from the top)
export const fitDetail = (text, cell, base) => {
  const parts = (text || "").split("\n").map(clean).filter(Boolean);
  const words = parts.join(", ").split(" ");
  if (!words[0]) return { size: base, lines: [] };
  const avail = cell.w - 2 * DETAIL_PAD_X;
  // Lines the cell holds at the full size: a one-line row stays one line (smaller text if need be)
  const room = Math.max(1, Math.floor((cell.h - 2 - base * DETAIL_CAP) / (base * DETAIL_LINE_GAP) + 1));
  const fitsIn = (lines, size) => lines.length <= room && lines.every((l) => textWidth(l, size) <= avail);
  const address = parts.length > 1;
  let size = base;
  let lines = address ? addressRows(parts, room).flatMap((row) => wrap(row.split(" "), size, avail)) : null;
  // Otherwise (and an address too long for its rows): flowing on, smaller only if it can't fit
  if (!lines || !fitsIn(lines, size)) {
    for (;;) {
      lines = wrap(words, size, avail);
      if (fitsIn(lines, size) || size <= DETAIL_MIN) break;
      size *= DETAIL_SHRINK;
    }
  }
  const lh = size * DETAIL_LINE_GAP;
  const top = address ? cell.y + DETAIL_TOP_PAD : cell.y + (cell.h - ((lines.length - 1) * lh + size * DETAIL_CAP)) / 2;
  return { size, lines: lines.map((t, i) => ({ x: cell.x + DETAIL_PAD_X, y: top + size * DETAIL_CAP + i * lh, text: t })) };
};
